import React from "react";
import { Card, Col, Row } from "antd";
import DateFilter from "./DateFilter";

/**
 * Generic antd stat-card row, with an optional heading/description above it.
 * Every field accepts a `className` to override/extend its default styling.
 *
 * @param {string} [heading]
 * @param {string} [description]
 * @param {string} [className]            — wrapper div
 * @param {string} [headingClassName]
 * @param {string} [descriptionClassName]
 * @param {string} [rowClassName]         — antd Row
 * @param {string} [cardClassName]        — default Card className, applied to every card
 * @param {Array<{
 *   title: string,
 *   value: number|string|React.ReactNode,
 *   subLabel?: string,
 *   suffix?: string,
 *   prefix?: string,
 *   className?: string,        — this card's Card
 *   titleClassName?: string,   — appended alongside the default title classes
 *   valueClassName?: string,   — appended alongside the default value classes
 *   prefixClassName?: string,  — appended alongside the default prefix classes
 *   suffixClassName?: string,  — appended alongside the default suffix classes
 *   subLabelClassName?: string,
 * }>} items
 *   Note: these are additive, not overrides — a conflicting Tailwind utility (e.g.
 *   passing "text-sm" against the default "text-2xl") isn't guaranteed to win, since
 *   Tailwind precedence depends on stylesheet order, not class-attribute order.
 * @param {boolean} [loading]
 * @param {boolean} [showDateFilter]        — render a <DateFilter /> above the cards
 * @param {(filter: string, range: {startDate, endDate}) => void} [onDateFilterChange]
 * @param {string} [dateFilterDefaultValue] — initial DATE_FILTER_OPTIONS value
 * @param {boolean} [hideMonthTillDate]     — passed through to <DateFilter />
 * @param {boolean} [hideLastMonth]         — passed through to <DateFilter />
 * @param {boolean} [hideLast90Days]        — passed through to <DateFilter />
 */
const AnalyticsSummaryCards = ({
    heading,
    description,
    className = "",
    headingClassName = "",
    descriptionClassName = "",
    rowClassName = "",
    cardClassName = "",
    items = [],
    loading = false,
    showDateFilter = false,
    onDateFilterChange,
    dateFilterDefaultValue,
    hideMonthTillDate = false,
    hideLastMonth = false,
    hideLast90Days = false,
}) => (
    <div className={`mb-6 ${className}`}>
        {showDateFilter && (
            <div className="mb-3 flex justify-end">
                <DateFilter
                    defaultValue={dateFilterDefaultValue}
                    hideMonthTillDate={hideMonthTillDate}
                    hideLastMonth={hideLastMonth}
                    hideLast90Days={hideLast90Days}
                    onChange={onDateFilterChange}
                />
            </div>
        )}
        {(heading || description) && (
            <div className="mb-3">
                {heading && (
                    <h2 className={`text-base font-semibold text-gray-900 ${headingClassName}`}>
                        {heading}
                    </h2>
                )}
                {description && (
                    <p className={`text-sm text-gray-500 mt-0.5 ${descriptionClassName}`}>
                        {description}
                    </p>
                )}
            </div>
        )}
        <Row gutter={[16, 16]} align="stretch" className={rowClassName}>
            {items.map((item) => (
                <Col xs={24} md={8} xl={24 / (items.length)} key={item.title} className="flex">
                    <Card
                        loading={loading}
                        className={`rounded-xl w-full h-full ${cardClassName} ${item.className || ""}`}
                    >
                        <div className={`block truncate text-lg font-semibold text-gray-400 ${item.titleClassName || ""}`}>
                            {item.title.toUpperCase()}
                        </div>
                        <div className="flex items-baseline space-x-1 mt-1">
                            {item.prefix !== undefined && (
                                <span className={`text-base font-bold text-gray-900 ${item.prefixClassName || ""}`}>
                                    {item.prefix}
                                </span>
                            )}
                            <div className={`text-base font-bold text-gray-900 ${item.valueClassName || ""}`}>
                                {item.value}
                            </div>
                            {item.suffix !== undefined && (
                                <span className={`text-base font-bold text-gray-900 ${item.suffixClassName || ""}`}>
                                    {item.suffix}
                                </span>
                            )}
                        </div>
                        {item.subLabel && (
                            <div className={`text-xs text-gray-400 mt-1 truncate ${item.subLabelClassName || ""}`}>
                                {item.subLabel}
                            </div>
                        )}
                    </Card>
                </Col>
            ))}
        </Row>
    </div>
);

export default AnalyticsSummaryCards;
